import { createContext, type FC, useCallback, useContext, useMemo, useState } from 'react';

import { errorHandler } from '@vakantiesnl/services/src/util/errorHandling';

import { useCheckAvailabilityRequest } from '../../dynamic-booking';
import { type AvailabilityRequestBody } from '../../dynamic-booking/build';
import { type availabilityCheckData } from '../../gtm';
import { useRestApi, type ApiActions } from '../../hooks/rest/useRestApi';

type ProviderProps = {
	children: React.ReactNode;
};

type AvailabilityStatus = 'Available' | 'NotAvailable';

export type AvailabilityCheckStructure = {
	new_session_created: boolean;
	price_total: number;
	session_id: string;
	status: {
		is_available: boolean;
		hint: string | null;
		flight: AvailabilityStatus | null;
		hotel: AvailabilityStatus | null;
	};
};

export type AvailabilityCheckStructureState = {
	checking: boolean;
	requiresNewPriceApproval?: boolean;
	available?: boolean;
	oldPrice?: number;
	newPrice?: number;
	sessionId?: string;
	flightAvailable?: boolean;
	hotelAvailable?: boolean;
	acceptedNewPrice?: boolean;
	additionalGTMData: Partial<availabilityCheckData>;
	didFinalCheckRequest: boolean;
};

export enum RequestType {
	OFFER_AVAILABILITY,
	FINAL_CHECK_REQUEST,
	EXPIRED_CHECK_REQUEST,
}

type AvailabilityRequestBodyProps = {
	requestBody: AvailabilityRequestBody;
	oldPrice: number;
	requestType: RequestType;
};

type ContextProps = {
	state: AvailabilityCheckStructureState;
	api: ApiActions<AvailabilityCheckStructure> & {
		setAvailabilityRequestBody: (requestBody?: AvailabilityRequestBodyProps) => void;
		acceptPriceChange: VoidFunction;
		setAdditionalGTMData: (GTMProps: Partial<availabilityCheckData>) => void;
	};
};

const tripComponentAvailable = (status?: string): boolean => status === 'Available';

const AvailabilityCheckContext = createContext<ContextProps>({} as ContextProps);

export const AvailabilityCheckContextProvider: FC<ProviderProps> = ({ children }) => {
	const [acceptedNewPrice, acceptNewPrice] = useState(false);
	const acceptPriceChange = useCallback((): void => acceptNewPrice(true), [acceptNewPrice]);
	const [additionalGTMData, setAdditionalGTMData] = useState<Partial<availabilityCheckData>>({});
	const [availabilityRequestBody, _setAvailabilityRequestBody] = useState<AvailabilityRequestBodyProps | undefined>();
	const [shouldLogError, setShouldLogError] = useState(false);

	const setAvailabilityRequestBody = useCallback((requestBody: AvailabilityRequestBodyProps | undefined) => {
		// Temp state to log any possible bugs related to WEB-5352
		if (requestBody?.requestType === RequestType.EXPIRED_CHECK_REQUEST) {
			setShouldLogError(true);
		} else {
			setShouldLogError(false);
		}

		_setAvailabilityRequestBody(requestBody);
	}, []);

	const request = useCheckAvailabilityRequest(
		availabilityRequestBody?.requestBody,
		availabilityRequestBody?.requestType === RequestType.FINAL_CHECK_REQUEST,
	);

	if (shouldLogError && !request) {
		errorHandler.report('IBE: request unexpectedly not set');
	}

	const [{ loading, data }, api] = useRestApi<AvailabilityCheckStructure>(request);
	const requestType = availabilityRequestBody?.requestType;
	const oldPrice = availabilityRequestBody?.oldPrice;

	const state = useMemo(
		() => ({
			checking: loading,
			available: data ? data.status.is_available : undefined,
			newPrice: data ? data.price_total : undefined,
			sessionId: data ? data.session_id : undefined,
			flightAvailable: data?.status.flight ? tripComponentAvailable(data.status.flight) : undefined,
			hotelAvailable: data?.status.hotel ? tripComponentAvailable(data.status.hotel) : undefined,
			acceptedNewPrice,
			oldPrice,
			requiresNewPriceApproval:
				data && requestType !== RequestType.FINAL_CHECK_REQUEST ? oldPrice !== data?.price_total : undefined,
			additionalGTMData,
			didFinalCheckRequest: data && requestType === RequestType.FINAL_CHECK_REQUEST ? true : false,
		}),
		[loading, data, acceptedNewPrice, oldPrice, requestType, additionalGTMData],
	);

	const value = useMemo(
		() => ({
			state,
			api: {
				...api,
				setAvailabilityRequestBody,
				acceptPriceChange,
				setAdditionalGTMData,
				resetState: (): void => {
					setShouldLogError(false);
					setAvailabilityRequestBody(undefined);
					acceptNewPrice(false);
					api.resetState();
					setAdditionalGTMData({});
				},
			},
		}),
		[setAvailabilityRequestBody, acceptPriceChange, state, api],
	);

	return <AvailabilityCheckContext.Provider value={value}>{children}</AvailabilityCheckContext.Provider>;
};

export const useAvailabilityCheckContext = (): ContextProps => useContext(AvailabilityCheckContext);
